import type { groups as groupContracts } from '@personal-blog/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, ApiRequestError } from '@/lib/api';
import {
  groupsKeys,
  inviteKeys,
  invalidateAllGroupsQueries,
  invalidateGroupMembershipDependents,
  type GroupListFilter,
} from './api-keys.js';

// Re-export the keys / filter type so existing call sites that import
// from `@/features/groups/api` keep compiling unchanged.
export { groupsKeys, inviteKeys };
export type { GroupListFilter };

// Domain modules — split out to keep this file under the 500-line cap
// and to give every domain (members, invites, applications) a dedicated
// home for hooks + variables types.
export * from './api-members.js';
export * from './api-invites.js';
export * from './api-applications.js';
export * from './api-engagement.js';

// ---------- queries: groups themselves ----------

export function useGroupCapabilities(): UseQueryResult<
  groupContracts.GroupCapabilitiesResponse,
  ApiRequestError
> {
  return useQuery({
    queryKey: groupsKeys.capabilities,
    queryFn: () =>
      apiFetch<groupContracts.GroupCapabilitiesResponse>('/api/groups/capabilities'),
  });
}

export interface UseGroupsListOptions {
  filter?: GroupListFilter;
  q?: string;
  tag?: string;
  limit?: number;
  offset?: number;
  /** When false, the query waits for explicit `refetch`. Useful for routes
   *  that should not fire while the auth-guard is still resolving. */
  enabled?: boolean;
}

export function useGroupsList(
  opts: UseGroupsListOptions = {},
): UseQueryResult<groupContracts.GroupListResponse, ApiRequestError> {
  const filter: GroupListFilter = opts.filter ?? 'active';
  const q = opts.q?.trim();
  const tag = opts.tag?.trim();
  const limit = opts.limit;
  const offset = opts.offset;
  return useQuery({
    queryKey: groupsKeys.list(filter, limit, offset, q, tag),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter !== 'active') qs.set('filter', filter);
      if (q !== undefined && q.length > 0) qs.set('q', q);
      if (tag !== undefined && tag.length > 0) qs.set('tag', tag);
      if (limit !== undefined) qs.set('limit', String(limit));
      if (offset !== undefined) qs.set('offset', String(offset));
      const suffix = qs.toString();
      return apiFetch<groupContracts.GroupListResponse>(
        `/api/groups${suffix.length > 0 ? `?${suffix}` : ''}`,
      );
    },
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    retry: false,
  });
}

export function useGroupDetail(
  slug: string | undefined,
): UseQueryResult<groupContracts.GroupResponse, ApiRequestError> {
  return useQuery({
    queryKey: groupsKeys.detail(slug ?? ''),
    queryFn: () =>
      apiFetch<groupContracts.GroupResponse>(`/api/groups/${encodeURIComponent(slug ?? '')}`),
    enabled: typeof slug === 'string' && slug.length > 0,
  });
}

export function useGroupRules(
  slug: string | undefined,
): UseQueryResult<groupContracts.RulesResponse, ApiRequestError> {
  return useQuery({
    queryKey: groupsKeys.rules(slug ?? ''),
    queryFn: () =>
      apiFetch<groupContracts.RulesResponse>(
        `/api/groups/${encodeURIComponent(slug ?? '')}/rules`,
      ),
    enabled: typeof slug === 'string' && slug.length > 0,
  });
}

export function useGroupFileSettings(
  slug: string | undefined,
): UseQueryResult<{ settings: groupContracts.GroupFileSettings }, ApiRequestError> {
  return useQuery({
    queryKey: groupsKeys.fileSettings(slug ?? ''),
    queryFn: () =>
      apiFetch<{ settings: groupContracts.GroupFileSettings }>(
        `/api/groups/${encodeURIComponent(slug ?? '')}/file-settings`,
      ),
    enabled: typeof slug === 'string' && slug.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export interface UpdateGroupFileSettingsVariables {
  slug: string;
  body: groupContracts.UpdateGroupFileSettingsRequest;
}

export function useUpdateGroupFileSettings(): UseMutationResult<
  { settings: groupContracts.GroupFileSettings },
  ApiRequestError,
  UpdateGroupFileSettingsVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }) =>
      apiFetch<{ settings: groupContracts.GroupFileSettings }>(
        `/api/groups/${encodeURIComponent(slug)}/file-settings`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: (data, variables) => {
      qc.setQueryData(groupsKeys.fileSettings(variables.slug), data);
      void qc.invalidateQueries({ queryKey: groupsKeys.detail(variables.slug) });
    },
  });
}

export interface ReplaceGroupRulesVariables {
  slug: string;
  body: groupContracts.ReplaceRulesRequest;
}

export function useReplaceGroupRules(): UseMutationResult<
  groupContracts.RulesResponse,
  ApiRequestError,
  ReplaceGroupRulesVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }) =>
      apiFetch<groupContracts.RulesResponse>(
        `/api/groups/${encodeURIComponent(slug)}/rules`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: (data, variables) => {
      qc.setQueryData(groupsKeys.rules(variables.slug), data);
      void qc.invalidateQueries({ queryKey: groupsKeys.detail(variables.slug) });
    },
  });
}

// ---------- mutations: create / update / archive / restore ----------

export function useCreateGroup(): UseMutationResult<
  groupContracts.GroupResponse,
  ApiRequestError,
  groupContracts.CreateGroupRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<groupContracts.GroupResponse>('/api/groups', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void invalidateAllGroupsQueries(qc);
    },
  });
}

export interface UpdateGroupVariables {
  slug: string;
  body: groupContracts.UpdateGroupRequest;
}

export function useUpdateGroup(): UseMutationResult<
  groupContracts.GroupResponse,
  ApiRequestError,
  UpdateGroupVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }) =>
      apiFetch<groupContracts.GroupResponse>(`/api/groups/${encodeURIComponent(slug)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: groupsKeys.detail(variables.slug) });
      void qc.invalidateQueries({ queryKey: ['groups', 'list'] });
    },
  });
}

export function useArchiveGroup(): UseMutationResult<
  groupContracts.GroupResponse,
  ApiRequestError,
  { slug: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }) =>
      apiFetch<groupContracts.GroupResponse>(`/api/groups/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateAllGroupsQueries(qc),
  });
}

export function useRestoreGroup(): UseMutationResult<
  groupContracts.GroupResponse,
  ApiRequestError,
  { slug: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }) =>
      apiFetch<groupContracts.GroupResponse>(`/api/groups/${encodeURIComponent(slug)}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateAllGroupsQueries(qc),
  });
}

// ---------- join / apply / leave (self) ----------

/** Self-join an open group. Returns the new membership row. */
export function useJoinGroup(): UseMutationResult<
  groupContracts.MemberResponse,
  ApiRequestError,
  { slug: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }) =>
      apiFetch<groupContracts.MemberResponse>(
        `/api/groups/${encodeURIComponent(slug)}/join`,
        { method: 'POST' },
      ),
    onSuccess: (_data, variables) => {
      invalidateGroupMembershipDependents(qc, variables.slug);
    },
  });
}

/** Submit an application to join a request-mode group. */
export function useApplyToGroup(): UseMutationResult<
  groupContracts.ApplicationResponse,
  ApiRequestError,
  { slug: string; message?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, message }) =>
      apiFetch<groupContracts.ApplicationResponse>(
        `/api/groups/${encodeURIComponent(slug)}/applications`,
        {
          method: 'POST',
          body: JSON.stringify({ message: message ?? null }),
        },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: groupsKeys.detail(variables.slug) });
      void qc.invalidateQueries({ queryKey: groupsKeys.applications(variables.slug) });
      void qc.invalidateQueries({ queryKey: ['groups', 'me', 'applications'] });
    },
  });
}

/** Leave a group (self). */
export function useLeaveGroup(): UseMutationResult<
  groupContracts.OkResponse,
  ApiRequestError,
  { slug: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }) =>
      apiFetch<groupContracts.OkResponse>(
        `/api/groups/${encodeURIComponent(slug)}/leave`,
        { method: 'POST' },
      ),
    onSuccess: (_data, variables) => {
      invalidateGroupMembershipDependents(qc, variables.slug);
    },
  });
}

// ---------- error helper ----------

export function groupsErrorKey(err: unknown): string {
  if (err instanceof ApiRequestError) return `errors.${err.code}`;
  return 'errors.UNKNOWN';
}
